import React, { useEffect, useState } from 'react';
import {
    Card,
    Row,
    Col,
    Statistic,
    Table,
    Tag,
    Space,
    Typography,
    Spin,
    Alert,
    Button,
    Modal,
    Form,
    Input,
    DatePicker,
    Select,
    InputNumber,
    message,
    Tabs,
    Progress,
    Descriptions,
    Upload,
    Image,
} from 'antd';
import {
    ProjectOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    FileTextOutlined,
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    CalendarOutlined,
    CheckCircleOutlined,
    TeamOutlined,
    UserOutlined,
    IdcardOutlined,
    UploadOutlined,
    EyeOutlined,
} from '@ant-design/icons';
import { useSelector } from 'react-redux';
import axios from 'axios';
import dayjs from 'dayjs';
import { getReceiptUrl } from '../../utils/urlConfig';

import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';

import { attendanceAPI } from '../../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { Option } = Select;
const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// 휴가 유형 설정
const TYPE_COLORS = {
    annual_leave: '#1890ff',
    sick_leave: '#ff4d4f',
    personal_leave: '#fa8c16',
    bereavement_leave: '#722ed1',
    half_day_morning: '#13c2c2',
    half_day_afternoon: '#2f54eb',
};

const TYPE_NAMES = {
    annual_leave: '연차',
    sick_leave: '병가',
    personal_leave: '건강검진',
    bereavement_leave: '경조사',
    half_day_morning: '오전반차',
    half_day_afternoon: '오후반차',
};

// rawEvents → FullCalendar events 변환
const convertToFCEvents = (rawEvents) => {
    // 1단계: 사용자+유형+상태별로 날짜 수집
    const groups = {};
    rawEvents.forEach(event => {
        const key = `${event.user.id}-${event.attendance_type}-${event.status}`;
        if (!groups[key]) {
            groups[key] = {
                user: event.user,
                attendance_type: event.attendance_type,
                status: event.status,
                reason: event.reason,
                dates: [],
            };
        }
        groups[key].dates.push(event.date);
    });

    const fcEvents = [];

    Object.values(groups).forEach(group => {
        // 날짜 정렬
        group.dates.sort();

        // 2단계: 연속된 날짜끼리만 묶기 (실제 연속된 날짜만)
        const segments = [];
        let currentSegment = [group.dates[0]];

        for (let i = 1; i < group.dates.length; i++) {
            const prev = dayjs(group.dates[i - 1]);
            const curr = dayjs(group.dates[i]);
            const diff = curr.diff(prev, 'day');

            // 실제 연속된 날짜만 묶기 (1일 차이만 연속으로 인정)
            if (diff === 1) {
                currentSegment.push(group.dates[i]);
            } else {
                // 주말을 건너뛴 경우 확인 (금요일 -> 월요일)
                const isPrevFriday = prev.day() === 5; // 금요일
                const isCurrMonday = curr.day() === 1;  // 월요일
                
                if (isPrevFriday && isCurrMonday && diff === 3) {
                    // 금요일 -> 월요일인 경우에만 연속으로 처리
                    currentSegment.push(group.dates[i]);
                } else {
                    // 그 외의 경우는 별도 세그먼트로 분리
                    segments.push(currentSegment);
                    currentSegment = [group.dates[i]];
                }
            }
        }
        segments.push(currentSegment);

        // 3단계: 각 segment를 별도 FC 이벤트로 생성
        segments.forEach(segment => {
            const startDate = segment[0];
            const endDate = dayjs(segment[segment.length - 1])
                .add(1, 'day')
                .format('YYYY-MM-DD');

            const color = group.status === 'approved'
                ? (TYPE_COLORS[group.attendance_type] || '#999')
                : '#bfbfbf';

            fcEvents.push({
                title: `${group.user.name}${group.status === 'pending' ? '*' : ''}`,
                start: startDate,
                end: endDate,
                backgroundColor: color,
                borderColor: color,
                textColor: '#fff',
                extendedProps: {
                    user: group.user,
                    attendance_type: group.attendance_type,
                    status: group.status,
                    reason: group.reason,
                    typeName: TYPE_NAMES[group.attendance_type] || group.attendance_type,
                    totalDays: segment.length,
                    startDate,
                    endDate: segment[segment.length - 1],
                },
            });
        });
    });

    return fcEvents;
};

// =====================
// 근태관리 탭 컴포넌트
// =====================
const AttendanceTab = ({ attendanceData, onRefresh }) => {
    const [createModalVisible, setCreateModalVisible] = useState(false);
    const [createLoading, setCreateLoading] = useState(false);
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [selectedAttendance, setSelectedAttendance] = useState(null);
    const [attendances, setAttendances] = useState([]);
    const [form] = Form.useForm();
    const [editForm] = Form.useForm();

    useEffect(() => {
        fetchAttendances();
    }, []);

    const fetchAttendances = async () => {
        try {
            const response = await attendanceAPI.getMy({ limit: 10 });
            console.log('Full API response:', response);
            console.log('Attendances data:', response.data.attendances);
            if (response.data.attendances && response.data.attendances.length > 0) {
                console.log('First attendance item:', response.data.attendances[0]);
            }
            setAttendances(response.data.attendances || []);
        } catch (error) {
            console.error('Fetch attendances error:', error);
        }
    };

    const handleCreateAttendance = async (values) => {
        setCreateLoading(true);
        try {
            const data = {
                ...values,
                start_date: values.date_range[0].format('YYYY-MM-DD'),
                end_date: values.date_range[1].format('YYYY-MM-DD'),
            };
            delete data.date_range;

            await attendanceAPI.create(data);
            message.success('근태 신청이 완료되었습니다');
            form.resetFields();
            setCreateModalVisible(false);
            fetchAttendances();
            onRefresh();
        } catch (error) {
            message.error(error.response?.data?.message || '근태 신청에 실패했습니다');
        } finally {
            setCreateLoading(false);
        }
    };

    // 근태 수정 모달 열기
    const handleEditAttendance = (attendance) => {
        setSelectedAttendance(attendance);
        
        // 폼에 기존 데이터 설정
        const startDate = dayjs(attendance.start_date);
        const endDate = dayjs(attendance.end_date);
        
        editForm.setFieldsValue({
            attendance_type: attendance.attendance_type,
            date_range: [startDate, endDate],
            reason: attendance.reason
        });
        
        setEditModalVisible(true);
    };

    // 근태 수정/재상신
    const handleUpdateAttendance = async (values) => {
        setEditLoading(true);
        try {
            const { attendance_type, date_range, reason } = values;
            
            const updateData = {
                attendance_type,
                start_date: date_range[0].format('YYYY-MM-DD'),
                end_date: date_range[1].format('YYYY-MM-DD'),
                reason
            };

            await attendanceAPI.update(selectedAttendance.id, updateData);
            message.success('근태 신청이 수정되어 재상신되었습니다');
            
            setEditModalVisible(false);
            editForm.resetFields();
            fetchAttendances();
            onRefresh();
        } catch (error) {
            message.error(error.response?.data?.message || '근태 수정 중 오류가 발생했습니다');
        } finally {
            setEditLoading(false);
        }
    };

    // 근태 삭제
    const handleDeleteAttendance = (attendance) => {
        Modal.confirm({
            title: '근태 삭제',
            content: `${TYPE_NAMES[attendance.attendance_type] || attendance.attendance_type} 신청을 삭제하시겠습니까?`,
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    await attendanceAPI.delete(attendance.id);
                    message.success('근태 신청이 삭제되었습니다');
                    fetchAttendances();
                    onRefresh();
                } catch (error) {
                    message.error(error.response?.data?.message || '근태 삭제 중 오료가 발생했습니다');
                }
            }
        });
    };

    const statusMap = {
        pending: { text: '승인대기', color: 'orange' },
        approved: { text: '승인완료', color: 'green' },
        rejected: { text: '반려', color: 'red' },
    };

    const attendanceColumns = [
        {
            title: '신청일',
            dataIndex: 'created_at',
            width: 140,
            render: (_, record) => {
                console.log('Record data:', record);
                console.log('created_at value:', record.created_at);
                console.log('updated_at value:', record.updated_at);
                console.log('created_at type:', typeof record.created_at);
                
                // created_at 값이 존재하고 유효한지 확인
                if (!record.created_at) {
                    console.warn('created_at is null/undefined for record:', record.id);
                    
                    // 임시로 updated_at 사용해보기
                    if (record.updated_at) {
                        console.log('Using updated_at as fallback:', record.updated_at);
                        return dayjs(record.updated_at).format('YYYY-MM-DD HH:mm');
                    }
                    
                    return '-';
                }
                
                // dayjs에 유효한 날짜인지 확인
                const dateValue = dayjs(record.created_at);
                if (!dateValue.isValid()) {
                    console.warn('Invalid date for created_at:', record.created_at);
                    return 'Invalid Date';
                }
                
                return dateValue.format('YYYY-MM-DD HH:mm');
            },
        },
        {
            title: '구분',
            dataIndex: 'attendance_type',
            width: 90,
            render: (type) => (
                <Tag color="blue">{TYPE_NAMES[type] || type}</Tag>
            ),
        },
        {
            title: '기간',
            width: 120,
            render: (_, record) => {
                const start = dayjs(record.start_date).format('MM-DD');
                const end = dayjs(record.end_date).format('MM-DD');
                return start === end ? start : `${start}~${end}`;
            },
        },
        {
            title: '일수',
            dataIndex: 'days_count',
            width: 60,
            align: 'center',
            render: (days) => `${days}일`,
        },
        {
            title: '사유',
            dataIndex: 'reason',
            width: 150,
            ellipsis: true,
            render: (reason) => (
                <div title={reason} style={{ 
                    maxWidth: '130px',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                }}>
                    {reason || '-'}
                </div>
            ),
        },
        {
            title: '상태',
            dataIndex: 'status',
            width: 80,
            render: (status) => {
                const s = statusMap[status] || { text: status, color: 'default' };
                return <Tag color={s.color}>{s.text}</Tag>;
            },
        },
        {
            title: '작업',
            key: 'action',
            width: 120,
            render: (_, record) => {
                const canEdit = ['pending', 'rejected'].includes(record.status);
                
                return (
                    <Space size="small">
                        {canEdit && (
                            <>
                                <Button 
                                    type="link" 
                                    size="small"
                                    onClick={() => handleEditAttendance(record)}
                                >
                                    수정
                                </Button>
                                <Button 
                                    type="link" 
                                    size="small"
                                    danger
                                    onClick={() => handleDeleteAttendance(record)}
                                >
                                    삭제
                                </Button>
                            </>
                        )}
                    </Space>
                );
            }
        }
    ];

    return (
        <div>
            {/* 연차 현황 카드 */}
            <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col xs={24} sm={8}>
                    <Card size="small">
                        <Statistic
                            title="총 연차"
                            value={attendanceData?.balance?.total_annual_leave || 15}
                            precision={1}
                            prefix={<CalendarOutlined />}
                            suffix="일"
                            valueStyle={{ color: '#1890ff', fontSize: 16 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card size="small">
                        <Statistic
                            title="사용 연차"
                            value={attendanceData?.balance?.used_annual_leave || 0}
                            precision={1}
                            prefix={<ClockCircleOutlined />}
                            suffix="일"
                            valueStyle={{ color: '#fa8c16', fontSize: 16 }}
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={8}>
                    <Card size="small">
                        <Statistic
                            title="잔여 연차"
                            value={attendanceData?.balance?.remaining_annual_leave || 15}
                            precision={1}
                            prefix={<CheckCircleOutlined />}
                            suffix="일"
                            valueStyle={{
                                color: (attendanceData?.balance?.remaining_annual_leave || 15) < 3
                                    ? '#cf1322' : '#52c41a',
                                fontSize: 16,
                            }}
                        />
                    </Card>
                </Col>
            </Row>

            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setCreateModalVisible(true)}
                >
                    근태 신청
                </Button>
            </div>

            <Table
                columns={attendanceColumns}
                dataSource={attendances}
                rowKey={(record) => record.id || record.created_at}
                size="small"
                pagination={false}
                scroll={{ x: 750 }}
                locale={{ emptyText: '신청된 근태가 없습니다' }}
            />

            {/* 근태 신청 모달 */}
            <Modal
                title="근태 신청"
                open={createModalVisible}
                onOk={() => form.submit()}
                onCancel={() => { setCreateModalVisible(false); form.resetFields(); }}
                confirmLoading={createLoading}
                okText="신청"
                cancelText="취소"
                width={500}
            >
                <Form form={form} layout="vertical" onFinish={handleCreateAttendance}>
                    <Form.Item
                        name="attendance_type"
                        label="근태 구분"
                        rules={[{ required: true, message: '근태 구분을 선택하세요' }]}
                    >
                        <Select placeholder="근태 구분 선택">
                            <Option value="annual_leave">연차</Option>
                            <Option value="half_day_morning">오전반차</Option>
                            <Option value="half_day_afternoon">오후반차</Option>
                            <Option value="sick_leave">병가</Option>
                            <Option value="bereavement_leave">경조휴가</Option>
                            <Option value="personal_leave">건강검진</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="date_range"
                        label="신청 기간"
                        rules={[{ required: true, message: '신청 기간을 선택하세요' }]}
                    >
                        <DatePicker.RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
                    </Form.Item>
                    <Form.Item
                        name="reason"
                        label="사유"
                        rules={[{ required: true, message: '사유를 입력하세요' }]}
                    >
                        <Input.TextArea rows={3} placeholder="근태 신청 사유를 입력하세요" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 근태 수정 모달 */}
            <Modal
                title="근태 수정"
                open={editModalVisible}
                onOk={() => editForm.submit()}
                onCancel={() => {
                    setEditModalVisible(false);
                    editForm.resetFields();
                    setSelectedAttendance(null);
                }}
                confirmLoading={editLoading}
                okText="수정"
                cancelText="취소"
                width={500}
            >
                <Form form={editForm} layout="vertical" onFinish={handleUpdateAttendance}>
                    <Form.Item
                        name="attendance_type"
                        label="근태 구분"
                        rules={[{ required: true, message: '근태 구분을 선택하세요' }]}
                    >
                        <Select placeholder="근태 구분 선택">
                            <Option value="annual_leave">연차</Option>
                            <Option value="half_day_morning">오전반차</Option>
                            <Option value="half_day_afternoon">오후반차</Option>
                            <Option value="sick_leave">병가</Option>
                            <Option value="bereavement_leave">경조휴가</Option>
                            <Option value="personal_leave">건강검진</Option>
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="date_range"
                        label="신청 기간"
                        rules={[{ required: true, message: '신청 기간을 선택하세요' }]}
                    >
                        <DatePicker.RangePicker 
                            style={{ width: '100%' }} 
                            format="YYYY-MM-DD"
                            disabledDate={(current) => {
                                return current && current < dayjs().startOf('day');
                            }}
                        />
                    </Form.Item>
                    <Form.Item
                        name="reason"
                        label="사유"
                    >
                        <Input.TextArea rows={3} placeholder="근태 신청 사유를 입력하세요" />
                    </Form.Item>
                    
                    <Alert
                        message="안내사항"
                        description="수정된 근태는 다시 승인 대기 상태로 변경되어 재승인이 필요합니다."
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                </Form>
            </Modal>
        </div>
    );
};

// =====================
// 메인 대시보드 페이지
// =====================
const DashboardPage = () => {
    const { user } = useSelector((state) => state.auth);
    const [loading, setLoading] = useState(true);
    const [dashboardData, setDashboardData] = useState(null);
    const [attendanceData, setAttendanceData] = useState(null);
    const [error, setError] = useState(null);

    // 휴가 캘린더
    const [holidayCalendarVisible, setHolidayCalendarVisible] = useState(false);
    const [calendarEvents, setCalendarEvents] = useState([]);
    const [calendarLoading, setCalendarLoading] = useState(false);

    // 이벤트 상세 모달
    const [eventDetailVisible, setEventDetailVisible] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // 작업시간 모달
    const [workHourModalVisible, setWorkHourModalVisible] = useState(false);
    const [workHourForm] = Form.useForm();
    const [editingWorkHour, setEditingWorkHour] = useState(null);

    // 개인경비 모달
    const [expenseModalVisible, setExpenseModalVisible] = useState(false);
    const [expenseForm] = Form.useForm();
    const [editingExpense, setEditingExpense] = useState(null);
    const [expenseFileList, setExpenseFileList] = useState([]);
    const [expenseFileUploading, setExpenseFileUploading] = useState(false);

    useEffect(() => {
        fetchDashboard();
    }, []);

    const fetchDashboard = async () => {
        setLoading(true);
        setError(null);
        try {
            const token = localStorage.getItem('token');
            const [dashboardResponse, attendanceResponse] = await Promise.all([
                axios.get(`${API_BASE}/dashboard/user`, {
                    headers: { Authorization: `Bearer ${token}` },
                }),
                attendanceAPI.getMyStatus(),
            ]);
            setDashboardData(dashboardResponse.data);
            setAttendanceData(attendanceResponse.data);
        } catch (error) {
            console.error('Dashboard fetch error:', error);
            setError(error.response?.data?.message || '대시보드 데이터를 불러올 수 없습니다');
        } finally {
            setLoading(false);
        }
    };

    // 캘린더 데이터 로드
    const loadCalendarData = async (dateInfo) => {
        setCalendarLoading(true);
        try {
            const current = dayjs(dateInfo.start).add(15, 'day');
            const params = { year: current.year(), month: current.month() + 1 };
            const response = await attendanceAPI.getTeamCalendar(params);
            const rawEvents = response.data?.data?.events || response.data?.events || [];
            setCalendarEvents(convertToFCEvents(rawEvents));
        } catch (error) {
            message.error('휴가 일정 조회에 실패했습니다');
            console.error('Calendar load error:', error);
        } finally {
            setCalendarLoading(false);
        }
    };

    // 캘린더 모달 열기 (초기 데이터 로드)
    const handleOpenHolidayCalendar = async () => {
        setHolidayCalendarVisible(true);
        setCalendarLoading(true);
        try {
            const now = dayjs();
            const params = { year: now.year(), month: now.month() + 1 };
            const response = await attendanceAPI.getTeamCalendar(params);
            const rawEvents = response.data?.data?.events || response.data?.events || [];
            setCalendarEvents(convertToFCEvents(rawEvents));
        } catch (error) {
            message.error('휴가 일정 조회에 실패했습니다');
        } finally {
            setCalendarLoading(false);
        }
    };

    // 이벤트 클릭 핸들러
    const handleEventClick = (clickInfo) => {
        setSelectedEvent(clickInfo.event);
        setEventDetailVisible(true);
    };

    // 작업시간 모달
    const handleOpenWorkHourModal = (record = null) => {
        if (record) {
            setEditingWorkHour(record);
            workHourForm.setFieldsValue({ ...record, work_date: dayjs(record.work_date) });
        } else {
            setEditingWorkHour(null);
            workHourForm.resetFields();
        }
        setWorkHourModalVisible(true);
    };

    const handleSubmitWorkHour = async () => {
        try {
            const values = await workHourForm.validateFields();
            const token = localStorage.getItem('token');
            const data = { ...values, work_date: values.work_date.format('YYYY-MM-DD') };

            if (editingWorkHour) {
                await axios.put(`${API_BASE}/work-hours/${editingWorkHour.id}`, data, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                message.success('작업시간이 수정되었습니다');
            } else {
                await axios.post(`${API_BASE}/work-hours`, data, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                message.success('작업시간이 등록되었습니다');
            }
            setWorkHourModalVisible(false);
            fetchDashboard();
        } catch (error) {
            message.error(error.response?.data?.message || '처리 실패');
        }
    };

    const handleDeleteWorkHour = async (id) => {
        Modal.confirm({
            title: '작업시간 삭제',
            content: '정말 삭제하시겠습니까?',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE}/work-hours/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    message.success('작업시간이 삭제되었습니다');
                    fetchDashboard();
                } catch (error) {
                    message.error(error.response?.data?.message || '삭제 실패');
                }
            },
        });
    };

    const handleDeleteExpense = async (id) => {
        Modal.confirm({
            title: '경비 삭제',
            content: '정말 삭제하시겠습니까?',
            okText: '삭제',
            okType: 'danger',
            cancelText: '취소',
            onOk: async () => {
                try {
                    const token = localStorage.getItem('token');
                    await axios.delete(`${API_BASE}/personal-expenses/${id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    message.success('경비가 삭제되었습니다');
                    fetchDashboard();
                } catch (error) {
                    message.error(error.response?.data?.message || '삭제 실패');
                }
            },
        });
    };

    // 개인경비 모달
    const handleOpenExpenseModal = (record = null) => {
        if (record) {
            setEditingExpense(record);
            expenseForm.setFieldsValue({ ...record, expense_date: dayjs(record.expense_date) });
            // 기존 영수증이 있으면 파일 리스트에 추가
            if (record.receipt_url) {
                const fullUrl = getReceiptUrl(record.receipt_url);
                setExpenseFileList([{
                    uid: '-1',
                    name: '영수증.jpg',
                    status: 'done',
                    url: fullUrl,
                }]);
            } else {
                setExpenseFileList([]);
            }
        } else {
            setEditingExpense(null);
            expenseForm.resetFields();
            setExpenseFileList([]);
            setExpenseFileUploading(false);
            
            // 새 경비 등록 시 사용자 기본 정보로 초기화
            if (user) {
                expenseForm.setFieldsValue({
                    bank_name: user.bank_name || '',
                    bank_account: user.bank_account || '',
                    account_holder: user.account_holder || user.name || ''
                });
            }
        }
        setExpenseModalVisible(true);
    };

    const handleSubmitExpense = async () => {
        try {
            const values = await expenseForm.validateFields();
            const token = localStorage.getItem('token');
            
            const formData = new FormData();
            formData.append('project_id', values.project_id);
            formData.append('expense_date', values.expense_date.format('YYYY-MM-DD'));
            formData.append('category', values.category);
            formData.append('vendor', values.vendor);
            formData.append('amount', values.amount);
            formData.append('purpose', values.purpose);
            formData.append('bank_name', values.bank_name);
            formData.append('bank_account', values.bank_account);
            formData.append('account_holder', values.account_holder);

            // 영수증 파일이 있으면 추가
            if (expenseFileList.length > 0) {
                const file = expenseFileList[0].originFileObj || expenseFileList[0];
                if (file && file instanceof File) {
                    formData.append('receipt', file);
                    console.log('📎 영수증 파일 첨부됨:', file.name, file.size);
                } else {
                    console.log('⚠️ 파일 객체가 유효하지 않음:', file);
                }
            } else {
                console.log('📎 첨부된 영수증 없음');
            }

            // FormData 내용 디버깅
            console.log('📤 전송할 FormData:');
            for (let pair of formData.entries()) {
                if (pair[1] instanceof File) {
                    console.log(`  ${pair[0]}: File(${pair[1].name}, ${pair[1].size} bytes)`);
                } else {
                    console.log(`  ${pair[0]}: ${pair[1]}`);
                }
            }

            if (editingExpense) {
                await axios.put(`${API_BASE}/personal-expenses/${editingExpense.id}`, formData, {
                    headers: { 
                        Authorization: `Bearer ${token}`
                        // Content-Type은 브라우저가 자동으로 설정하도록 함 (boundary 포함)
                    },
                });
                message.success('경비가 수정되었습니다');
            } else {
                await axios.post(`${API_BASE}/personal-expenses`, formData, {
                    headers: { 
                        Authorization: `Bearer ${token}`
                        // Content-Type은 브라우저가 자동으로 설정하도록 함 (boundary 포함)
                    },
                });
                message.success('경비가 등록되었습니다');
            }
            setExpenseModalVisible(false);
            setExpenseFileList([]);
            fetchDashboard();
        } catch (error) {
            message.error(error.response?.data?.message || '처리 실패');
        }
    };

    if (loading) {
        return (
            <div style={{ textAlign: 'center', padding: '100px 0' }}>
                <Spin size="large" />
            </div>
        );
    }

    if (error) {
        return <Alert message="오류" description={error} type="error" showIcon />;
    }

    if (!dashboardData) {
        return <Alert message="데이터 없음" description="대시보드 데이터를 불러올 수 없습니다" type="warning" showIcon />;
    }

    // 작업시간 컬럼
    const workHourColumns = [
        {
            title: '작업일',
            dataIndex: 'work_date',
            key: 'work_date',
            width: 110,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            ellipsis: true,
        },
        {
            title: '작업시간',
            dataIndex: 'hours',
            key: 'hours',
            width: 90,
            align: 'right',
            render: (hours) => `${hours}시간`,
        },
        {
            title: '작업내용',
            dataIndex: 'description',
            key: 'description',
            ellipsis: true,
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            width: 90,
            render: (status) => {
                const map = {
                    pending: { text: '대기', color: 'orange' },
                    approved: { text: '승인', color: 'green' },
                    rejected: { text: '반려', color: 'red' },
                };
                const s = map[status] || { text: status, color: 'default' };
                return <Tag color={s.color}>{s.text}</Tag>;
            },
        },
        {
            title: '작업',
            key: 'action',
            width: 120,
            render: (_, record) =>
                record.status === 'pending' ? (
                    <Space size="small">
                        <Button type="link" size="small" icon={<EditOutlined />}
                                onClick={() => handleOpenWorkHourModal(record)}>수정</Button>
                        <Button type="link" size="small" danger icon={<DeleteOutlined />}
                                onClick={() => handleDeleteWorkHour(record.id)}>삭제</Button>
                    </Space>
                ) : null,
        },
    ];

    // 개인경비 컬럼
    const expenseColumns = [
        {
            title: '사용일',
            dataIndex: 'expense_date',
            key: 'expense_date',
            width: 110,
            render: (date) => dayjs(date).format('YYYY-MM-DD'),
        },
        {
            title: '프로젝트',
            dataIndex: ['project', 'name'],
            key: 'project',
            ellipsis: true,
        },
        {
            title: '구분',
            dataIndex: 'category',
            key: 'category',
            width: 80,
            render: (c) => ({ transport: '교통', meal: '식비', supplies: '용품', other: '기타' }[c] || c),
        },
        {
            title: '금액',
            dataIndex: 'amount',
            key: 'amount',
            width: 100,
            align: 'right',
            render: (amount) => `${amount.toLocaleString()}원`,
        },
        {
            title: '용도',
            dataIndex: 'purpose',
            key: 'purpose',
            ellipsis: true,
        },
        {
            title: '영수증',
            dataIndex: 'receipt_url',
            key: 'receipt_url',
            width: 80,
            align: 'center',
            render: (receipt_url) => (
                receipt_url ? (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
                        <Text style={{ color: '#52c41a', fontWeight: 'bold', fontSize: 12 }}>O</Text>
                        <Image
                            width={30}
                            height={30}
                            src={getReceiptUrl(receipt_url)}
                            fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg=="
                            style={{ borderRadius: 4 }}
                            preview={{
                                mask: (
                                    <div style={{ fontSize: 10 }}>
                                        <EyeOutlined />
                                    </div>
                                )
                            }}
                        />
                    </div>
                ) : (
                    <Text style={{ color: '#ff4d4f', fontWeight: 'bold', fontSize: 14 }}>X</Text>
                )
            ),
        },
        {
            title: '상태',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status) => {
                const map = {
                    pm_pending: { text: 'PM대기', color: 'orange' },
                    finance_pending: { text: '재무대기', color: 'blue' },
                    paid: { text: '완료', color: 'green' },
                    rejected: { text: '반려', color: 'red' },
                };
                const s = map[status] || { text: status, color: 'default' };
                return <Tag color={s.color}>{s.text}</Tag>;
            },
        },
        {
            title: '작업',
            key: 'action',
            width: 150,
            render: (_, record) => {
                // 수정/삭제 가능한 상태: pm_pending, rejected
                const canEdit = ['pm_pending', 'rejected'].includes(record.status);
                
                if (!canEdit) return null;

                return (
                    <Space size="small">
                        <Button 
                            type="link" 
                            size="small" 
                            icon={<EditOutlined />}
                            onClick={() => handleOpenExpenseModal(record)}
                            style={{ color: record.status === 'rejected' ? '#ff4d4f' : undefined }}
                        >
                            {record.status === 'rejected' ? '재제출' : '수정'}
                        </Button>
                        <Button 
                            type="link" 
                            size="small" 
                            danger 
                            icon={<DeleteOutlined />}
                            onClick={() => handleDeleteExpense(record.id)}
                        >
                            삭제
                        </Button>
                    </Space>
                );
            },
        },
    ];

    return (
        <div>
            {/* 헤더 */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <Title level={2}>📊 내 대시보드</Title>
                <Button type="default" icon={<TeamOutlined />} onClick={handleOpenHolidayCalendar}>
                    휴가현황
                </Button>
            </div>

            {/* 사용자 정보 카드 */}
            {user && (
                <Card 
                    title={
                        <Space>
                            <UserOutlined />
                            내 정보
                        </Space>
                    }
                    size="small"
                    style={{ marginBottom: 16 }}
                >
                    <Descriptions 
                        size="small" 
                        column={{ xs: 1, sm: 2, md: 4 }}
                        labelStyle={{ color: '#666', fontWeight: 'normal' }}
                    >
                        <Descriptions.Item label="이름">
                            <Text strong>{user.name}</Text>
                        </Descriptions.Item>
                        <Descriptions.Item label="사번">
                            <Space>
                                <IdcardOutlined style={{ color: '#1890ff' }} />
                                {user.employee_id}
                            </Space>
                        </Descriptions.Item>
                        <Descriptions.Item label="부서">
                            <Tag color="blue">{user.department?.name || '-'}</Tag>
                        </Descriptions.Item>
                        <Descriptions.Item label="입사일">
                            <Space>
                                <CalendarOutlined style={{ color: '#52c41a' }} />
                                {user.hire_date ? dayjs(user.hire_date).format('YYYY-MM-DD') : '-'}
                            </Space>
                        </Descriptions.Item>
                    </Descriptions>
                </Card>
            )}

            {/* KPI 카드 */}
            <Row gutter={16} style={{ marginTop: 24 }}>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="투입 프로젝트"
                            value={dashboardData?.projects?.count || 0}
                            prefix={<ProjectOutlined />}
                            suffix="건"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="이번 주 작업시간"
                            value={dashboardData?.work_hours?.weekly || 0}
                            precision={1}
                            prefix={<ClockCircleOutlined />}
                            suffix="시간"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="이번 달 작업시간"
                            value={dashboardData?.work_hours?.monthly || 0}
                            precision={1}
                            prefix={<FileTextOutlined />}
                            suffix="시간"
                        />
                    </Card>
                </Col>
                <Col xs={24} sm={12} lg={6}>
                    <Card>
                        <Statistic
                            title="미입금 경비"
                            value={dashboardData?.expenses?.unpaid_amount || 0}
                            precision={0}
                            prefix={<DollarOutlined />}
                            suffix="원"
                            valueStyle={{ color: '#cf1322' }}
                        />
                    </Card>
                </Col>
            </Row>

            {/* 투입 프로젝트 현황 */}
            <Row gutter={16} style={{ marginTop: 24 }}>
                <Col span={24}>
                    <Card title="💼 투입 프로젝트 현황" size="small">
                        {dashboardData?.projects?.list?.length > 0 ? (
                            <Row gutter={16}>
                                {dashboardData.projects.list.map((item) => (
                                    <Col xs={24} md={12} lg={8} key={item.id} style={{ marginBottom: 16 }}>
                                        <Card size="small" bordered>
                                            <Space direction="vertical" style={{ width: '100%' }} size="small">
                                                <Text strong style={{ fontSize: 16 }}>{item.project?.name}</Text>
                                                <Space wrap>
                                                    <Tag color="blue">{item.project?.code}</Tag>
                                                    <Tag>{item.role}</Tag>
                                                    <Tag color="purple">투입율 {item.allocation_rate}%</Tag>
                                                </Space>
                                                <div>
                                                    <Text type="secondary" style={{ fontSize: 12 }}>
                                                        공수: {item.used_hours?.toFixed(1) || 0} / {item.allocated_hours}h
                                                    </Text>
                                                    <Progress
                                                        percent={parseFloat((item.usage_rate || 0).toFixed(1))}
                                                        size="small"
                                                        status={item.usage_rate > 100 ? 'exception' : 'active'}
                                                    />
                                                </div>
                                                
                                                {/* 공정관리 버튼 */}
                                                <div style={{ marginTop: 8 }}>
                                                    {item.project?.flow_link ? (
                                                        <Button
                                                            type="primary"
                                                            size="small"
                                                            onClick={() => window.open(item.project.flow_link, '_blank')}
                                                            style={{
                                                                backgroundColor: '#52c41a',
                                                                borderColor: '#52c41a',
                                                                width: '100%'
                                                            }}
                                                        >
                                                            🔗 공정관리 플로우
                                                        </Button>
                                                    ) : (
                                                        <Button
                                                            type="dashed"
                                                            size="small"
                                                            disabled
                                                            style={{ width: '100%' }}
                                                        >
                                                            공정관리 미설정
                                                        </Button>
                                                    )}
                                                </div>
                                            </Space>
                                        </Card>
                                    </Col>
                                ))}
                            </Row>
                        ) : (
                            <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                                투입된 프로젝트가 없습니다
                            </div>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* 근태관리 / 개인경비 / 작업시간 탭 */}
            <Row gutter={16} style={{ marginTop: 24 }}>
                <Col span={24}>
                    <Card size="small">
                        <Tabs
                            items={[
                                {
                                    key: 'attendance',
                                    label: '📅 근태관리',
                                    children: (
                                        <AttendanceTab
                                            attendanceData={attendanceData}
                                            onRefresh={fetchDashboard}
                                        />
                                    ),
                                },
                                {
                                    key: 'expenses',
                                    label: '💰 개인경비',
                                    children: (
                                        <div>
                                            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                                                <Button type="primary" icon={<PlusOutlined />}
                                                        onClick={() => handleOpenExpenseModal()}>
                                                    경비 등록
                                                </Button>
                                            </div>
                                            <Table
                                                columns={expenseColumns}
                                                dataSource={dashboardData?.expenses?.recent || []}
                                                rowKey="id"
                                                size="small"
                                                pagination={false}
                                                locale={{ emptyText: '등록된 경비가 없습니다' }}
                                            />
                                        </div>
                                    ),
                                },
                                {
                                    key: 'workhours',
                                    label: '⏰ 작업시간',
                                    children: (
                                        <div>
                                            <div style={{ marginBottom: 16, textAlign: 'right' }}>
                                                <Button type="primary" icon={<PlusOutlined />}
                                                        onClick={() => handleOpenWorkHourModal()}>
                                                    작업시간 등록
                                                </Button>
                                            </div>
                                            <Table
                                                columns={workHourColumns}
                                                dataSource={dashboardData?.work_hours?.recent || []}
                                                rowKey="id"
                                                size="small"
                                                pagination={false}
                                                locale={{ emptyText: '등록된 작업시간이 없습니다' }}
                                            />
                                        </div>
                                    ),
                                },
                            ]}
                        />
                    </Card>
                </Col>
            </Row>

            {/* FullCalendar 휴가 현황 모달 */}
            <Modal
                title="팀 휴가 현황"
                open={holidayCalendarVisible}
                onCancel={() => setHolidayCalendarVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setHolidayCalendarVisible(false)}>닫기</Button>,
                ]}
                width={900}
                style={{ top: 20 }}
                afterOpenChange={(open) => {
                    if (open) {
                        // 모달 완전히 열린 후 FullCalendar 크기 재계산
                        setTimeout(() => {
                            window.dispatchEvent(new Event('resize'));
                        }, 100);
                    }
                }}
            >
                <Spin spinning={calendarLoading}>
                    {/* 범례 */}
                    <div style={{ marginBottom: 12, textAlign: 'center' }}>
                        <Space wrap>
                            {Object.entries(TYPE_NAMES).map(([key, name]) => (
                                <span key={key} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                                    <span style={{
                                        display: 'inline-block',
                                        width: 12, height: 12,
                                        borderRadius: 2,
                                        backgroundColor: TYPE_COLORS[key],
                                    }} />
                                    <span style={{ fontSize: 12 }}>{name}</span>
                                </span>
                            ))}
                            <span style={{ color: '#bfbfbf', fontSize: 12 }}>│ 회색: 신청중</span>
                        </Space>
                    </div>

                    {/* FullCalendar CSS */}
                    <style>{`
                        .fc .fc-toolbar-title { font-size: 16px; }
                        .fc .fc-button { font-size: 12px; padding: 4px 10px; }
                        .fc .fc-daygrid-event {
                            border-radius: 4px;
                            font-size: 11px;
                            font-weight: bold;
                            padding: 1px 4px;
                            cursor: pointer;
                        }
                        .fc .fc-event:hover { opacity: 0.85; }
                        .fc .fc-col-header-cell { background: #f5f5f5; }
                        .fc .fc-day-today { background: #e6f4ff !important; }
                    `}</style>

                    <FullCalendar
                        plugins={[dayGridPlugin, interactionPlugin]}
                        initialView="dayGridMonth"
                        locale="ko"
                        events={calendarEvents}
                        height={580}
                        headerToolbar={{
                            left: 'prev,next today',
                            center: 'title',
                            right: '',
                        }}
                        buttonText={{ today: '오늘' }}
                        eventClick={handleEventClick}
                        datesSet={loadCalendarData}
                        eventDisplay="block"
                        dayMaxEvents={4}
                        moreLinkText={(n) => `+${n}명 더보기`}
                    />
                </Spin>
            </Modal>

            {/* 이벤트 상세 모달 */}
            <Modal
                title={
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <CalendarOutlined />
                        <span>휴가 상세 정보</span>
                    </div>
                }
                open={eventDetailVisible}
                onCancel={() => setEventDetailVisible(false)}
                footer={[
                    <Button key="close" onClick={() => setEventDetailVisible(false)}>닫기</Button>,
                ]}
                width={400}
            >
                {selectedEvent && (() => {
                    const props = selectedEvent.extendedProps;
                    return (
                        <div>
                            <div style={{
                                backgroundColor: selectedEvent.backgroundColor,
                                color: '#fff',
                                padding: '8px 12px',
                                borderRadius: 6,
                                marginBottom: 16,
                                fontWeight: 'bold',
                                fontSize: 15,
                            }}>
                                {props.user.name} - {props.typeName}
                            </div>
                            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                <tbody>
                                {[
                                    ['사번', props.user.employee_id],
                                    ['부서', props.user.department?.name || '-'],
                                    ['기간', `${props.startDate} ~ ${props.endDate}`],
                                    ['일수', `${props.totalDays}일`],
                                    ['상태', props.status === 'approved'
                                        ? <Tag color="green">승인완료</Tag>
                                        : <Tag color="orange">신청중</Tag>],
                                    ['사유', props.reason || '-'],
                                ].map(([label, value]) => (
                                    <tr key={label} style={{ borderBottom: '1px solid #f0f0f0' }}>
                                        <td style={{
                                            padding: '8px 12px',
                                            color: '#888',
                                            fontSize: 13,
                                            width: 60,
                                            whiteSpace: 'nowrap',
                                        }}>{label}</td>
                                        <td style={{ padding: '8px 12px', fontSize: 13 }}>{value}</td>
                                    </tr>
                                ))}
                                </tbody>
                            </table>
                        </div>
                    );
                })()}
            </Modal>

            {/* 작업시간 등록/수정 모달 */}
            <Modal
                title={editingWorkHour ? '작업시간 수정' : '작업시간 등록'}
                open={workHourModalVisible}
                onOk={handleSubmitWorkHour}
                onCancel={() => setWorkHourModalVisible(false)}
                okText={editingWorkHour ? '수정' : '등록'}
                cancelText="취소"
            >
                <Form form={workHourForm} layout="vertical">
                    <Form.Item name="project_id" label="프로젝트"
                               rules={[{ required: true, message: '프로젝트를 선택하세요' }]}>
                        <Select placeholder="프로젝트 선택">
                            {dashboardData?.projects?.list.map(p => (
                                <Select.Option key={p.project_id} value={p.project_id}>
                                    {p.project?.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="work_date" label="작업일"
                               rules={[{ required: true, message: '작업일을 선택하세요' }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="hours" label="작업시간"
                               rules={[{ required: true, message: '작업시간을 입력하세요' }]}>
                        <InputNumber min={0.5} max={24} step={0.5} style={{ width: '100%' }} addonAfter="시간" />
                    </Form.Item>
                    <Form.Item name="description" label="작업내용"
                               rules={[{ required: true, message: '작업내용을 입력하세요' }]}>
                        <TextArea rows={4} placeholder="작업 내용을 입력하세요" />
                    </Form.Item>
                </Form>
            </Modal>

            {/* 개인경비 등록/수정 모달 */}
            <Modal
                title={
                    editingExpense && editingExpense.status === 'rejected' 
                        ? '🔄 반려된 경비 재제출' 
                        : editingExpense 
                            ? '경비 수정' 
                            : '경비 등록'
                }
                open={expenseModalVisible}
                onOk={handleSubmitExpense}
                onCancel={() => setExpenseModalVisible(false)}
                okText={
                    editingExpense && editingExpense.status === 'rejected' 
                        ? '재제출' 
                        : editingExpense 
                            ? '수정' 
                            : '등록'
                }
                cancelText="취소"
            >
                {/* 반려된 경비인 경우 안내 메시지 */}
                {editingExpense && editingExpense.status === 'rejected' && (
                    <Alert
                        message="반려 사유"
                        description={
                            <div>
                                <div style={{ marginBottom: 8, color: '#ff4d4f', fontWeight: 'bold' }}>
                                    {editingExpense.rejection_reason}
                                </div>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    💡 위 사유를 참고하여 내용을 수정한 후 재제출해주세요. 
                                    재제출 시 PM 승인 대기 상태로 변경됩니다.
                                </div>
                            </div>
                        }
                        type="error"
                        showIcon
                        style={{ marginBottom: 16 }}
                    />
                )}
                
                <Form form={expenseForm} layout="vertical">
                    <Form.Item name="project_id" label="프로젝트"
                               rules={[{ required: true, message: '프로젝트를 선택하세요' }]}>
                        <Select placeholder="프로젝트 선택">
                            {dashboardData?.projects?.list.map(p => (
                                <Select.Option key={p.project_id} value={p.project_id}>
                                    {p.project?.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item name="expense_date" label="사용일"
                               rules={[{ required: true, message: '사용일을 선택하세요' }]}>
                        <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item name="category" label="구분"
                               rules={[{ required: true, message: '구분을 선택하세요' }]}>
                        <Select placeholder="구분 선택">
                            <Select.Option value="transport">교통비</Select.Option>
                            <Select.Option value="meal">식비</Select.Option>
                            <Select.Option value="supplies">용품비</Select.Option>
                            <Select.Option value="other">기타</Select.Option>
                        </Select>
                    </Form.Item>
                    <Form.Item name="vendor" label="사용처"
                               rules={[{ required: true, message: '사용처를 입력하세요' }]}>
                        <Input placeholder="예) 카카오택시, 스타벅스 등" />
                    </Form.Item>
                    <Form.Item name="amount" label="금액"
                               rules={[{ required: true, message: '금액을 입력하세요' }]}>
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            formatter={v => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                            parser={v => v.replace(/\$\s?|(,*)/g, '')}
                            addonAfter="원"
                        />
                    </Form.Item>
                    <Form.Item name="purpose" label="사용목적"
                               rules={[{ required: true, message: '사용목적을 입력하세요' }]}>
                        <TextArea rows={3} placeholder="사용 목적을 입력하세요" />
                    </Form.Item>
                    
                    {/* 은행 정보 섹션 */}
                    <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        padding: '16px', 
                        borderRadius: '8px', 
                        border: '1px solid #e9ecef',
                        marginBottom: '16px'
                    }}>
                        <div style={{ 
                            fontSize: '14px', 
                            fontWeight: '600', 
                            color: '#495057',
                            marginBottom: '12px',
                            display: 'flex',
                            alignItems: 'center'
                        }}>
                            💳 입금받을 계좌 정보
                            <span style={{ 
                                fontSize: '12px', 
                                fontWeight: 'normal', 
                                color: '#6c757d',
                                marginLeft: '8px'
                            }}>
                                (기본 정보가 자동으로 입력됩니다)
                            </span>
                        </div>
                        
                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item name="bank_name" label="은행명"
                                           rules={[{ required: true, message: '은행명을 입력하세요' }]}>
                                    <Select
                                        placeholder="은행 선택"
                                        showSearch
                                        allowClear
                                        optionFilterProp="children"
                                    >
                                        <Select.Option value="국민은행">국민은행</Select.Option>
                                        <Select.Option value="신한은행">신한은행</Select.Option>
                                        <Select.Option value="우리은행">우리은행</Select.Option>
                                        <Select.Option value="하나은행">하나은행</Select.Option>
                                        <Select.Option value="농협은행">농협은행</Select.Option>
                                        <Select.Option value="기업은행">기업은행</Select.Option>
                                        <Select.Option value="외환은행">외환은행</Select.Option>
                                        <Select.Option value="시티은행">시티은행</Select.Option>
                                        <Select.Option value="대구은행">대구은행</Select.Option>
                                        <Select.Option value="부산은행">부산은행</Select.Option>
                                        <Select.Option value="경남은행">경남은행</Select.Option>
                                        <Select.Option value="광주은행">광주은행</Select.Option>
                                        <Select.Option value="전북은행">전북은행</Select.Option>
                                        <Select.Option value="제주은행">제주은행</Select.Option>
                                        <Select.Option value="새마을금고">새마을금고</Select.Option>
                                        <Select.Option value="신협">신협</Select.Option>
                                        <Select.Option value="우체국">우체국</Select.Option>
                                        <Select.Option value="카카오뱅크">카카오뱅크</Select.Option>
                                        <Select.Option value="케이뱅크">케이뱅크</Select.Option>
                                        <Select.Option value="토스뱅크">토스뱅크</Select.Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                            <Col span={10}>
                                <Form.Item name="bank_account" label="계좌번호"
                                           rules={[{ required: true, message: '계좌번호를 입력하세요' }]}>
                                    <Input placeholder="예) 110-123-456789" />
                                </Form.Item>
                            </Col>
                            <Col span={6}>
                                <Form.Item name="account_holder" label="예금주"
                                           rules={[{ required: true, message: '예금주를 입력하세요' }]}>
                                    <Input placeholder="본인 이름" />
                                </Form.Item>
                            </Col>
                        </Row>
                        
                        <div style={{ 
                            fontSize: '12px', 
                            color: '#6c757d',
                            marginTop: '8px'
                        }}>
                            💡 계좌 정보는 언제든 수정할 수 있으며, 승인 후 해당 계좌로 입금됩니다.
                        </div>
                    </div>
                    
                    <Form.Item label="영수증 첨부">
                        <Upload
                            fileList={expenseFileList}
                            beforeUpload={(file) => {
                                // 파일 형식 검증
                                const isValidFormat = file.type.startsWith('image/') || file.type === 'application/pdf';
                                if (!isValidFormat) {
                                    message.error('JPG, PNG, PDF 형식의 파일만 업로드 가능합니다');
                                    return false;
                                }
                                
                                // 파일 크기 검증 (10MB)
                                const isValidSize = file.size / 1024 / 1024 < 10;
                                if (!isValidSize) {
                                    message.error('파일 크기는 10MB 이하여야 합니다');
                                    return false;
                                }
                                
                                // 로딩 상태 시작
                                setExpenseFileUploading(true);
                                
                                // 파일 처리 시뮬레이션 (실제 처리 시간을 반영)
                                setTimeout(() => {
                                    // 파일 리스트에 추가 (originFileObj 속성 포함)
                                    setExpenseFileList([{
                                        uid: file.uid,
                                        name: file.name,
                                        status: 'done',
                                        originFileObj: file
                                    }]);
                                    setExpenseFileUploading(false);
                                    console.log('📎 파일 선택됨:', file.name, file.size);
                                }, 500); // 0.5초 딜레이
                                
                                return false; // 자동 업로드 방지
                            }}
                            onRemove={() => {
                                setExpenseFileList([]);
                            }}
                            maxCount={1}
                        >
                            <Button 
                                icon={expenseFileUploading ? <Spin size="small" /> : <UploadOutlined />}
                                loading={expenseFileUploading}
                                disabled={expenseFileUploading}
                            >
                                {expenseFileUploading ? '처리 중...' : '영수증 선택'}
                            </Button>
                        </Upload>
                        <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
                            JPG, PNG, PDF 형식 (최대 10MB)
                        </div>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DashboardPage;